'use client';

import { useState, useEffect, useMemo } from 'react';
import { LuArrowRightLeft } from 'react-icons/lu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@core/ui/primitives/alert-dialog';
import { Button } from '@core/ui/primitives/button';
import { Input } from '@core/ui/primitives/input';
import { Label } from '@core/ui/primitives/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@core/ui/primitives/select';
import { Spinner } from '@core/ui/primitives/spinner';
import { useToast } from '@/core/ui/hooks/use-toast';

interface Organization {
  id: string;
  name: string;
  slug: string;
  organizationRole: string | null;
  privileges: string[];
  vaults: Array<{ id: string; slug: string }>;
}

interface MoveVaultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vaultId: string;
  vaultSlug: string;
  vaultTitle: string;
  currentOrganizationId: string;
  onMoved?: () => void | Promise<void>;
}

export function MoveVaultDialog({
  open,
  onOpenChange,
  vaultId,
  vaultSlug,
  vaultTitle,
  currentOrganizationId,
  onMoved,
}: MoveVaultDialogProps) {
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [newSlug, setNewSlug] = useState<string>('');
  const [slugConflict, setSlugConflict] = useState<boolean>(false);

  // Fetch organizations user owns (excluding current)
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      try {
        const response = await fetch('/api/vaults/me', { signal: controller.signal });
        if (!response.ok) throw new Error('Failed to load organizations');
        const data = await response.json();

        if (cancelled) return;

        // Filter to orgs where user can move vaults and exclude current org
        const canMoveOrgs = (data.organizations ?? []).filter(
          (org: Organization) =>
            (org.privileges ?? []).includes('org:vault:move') && org.id !== currentOrganizationId
        );
        setOrganizations(canMoveOrgs);

        // Pre-select first available org
        if (canMoveOrgs.length > 0) {
          setTargetOrgId(canMoveOrgs[0].id);
        }
      } catch (err) {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load organizations',
            variant: 'destructive',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, currentOrganizationId, toast]);

  // Check for slug conflicts in target org
  const targetOrg = useMemo(
    () => organizations.find((org) => org.id === targetOrgId),
    [organizations, targetOrgId]
  );

  useEffect(() => {
    if (!targetOrg) {
      setSlugConflict(false);
      return;
    }

    const slugToCheck = newSlug.trim() || vaultSlug;
    const conflict = targetOrg.vaults.some(
      (v) => v.slug.toLowerCase() === slugToCheck.toLowerCase()
    );
    setSlugConflict(conflict);
  }, [targetOrg, newSlug, vaultSlug]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTargetOrgId('');
      setNewSlug('');
      setSlugConflict(false);
    }
  }, [open]);

  const handleMove = async () => {
    if (!targetOrgId || slugConflict) return;

    setIsMoving(true);
    try {
      const body: { targetOrganizationId: string; newSlug?: string } = {
        targetOrganizationId: targetOrgId,
      };
      if (newSlug.trim()) {
        body.newSlug = newSlug.trim();
      }

      const response = await fetch(`/api/vaults/${vaultId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data.message || data.error || 'Failed to move vault';
        throw new Error(message);
      }

      toast({
        title: 'Vault moved',
        description: `"${vaultTitle}" has been moved to ${targetOrg?.name ?? 'the new organization'}.`,
        variant: 'success',
      });

      onOpenChange(false);
      await onMoved?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsMoving(false);
    }
  };

  const canMove = targetOrgId && !slugConflict && !isMoving;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className='flex items-center gap-2'>
            <LuArrowRightLeft className='h-5 w-5' />
            Move Vault
          </AlertDialogTitle>
          <AlertDialogDescription>
            Move &quot;{vaultTitle}&quot; to another organization you own. All content, memberships, and settings will
            transfer with the vault.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {loading ? (
          <div className='flex items-center justify-center py-8'>
            <Spinner className='h-6 w-6' />
          </div>
        ) : organizations.length === 0 ? (
          <div className='py-4 text-sm text-muted-foreground'>
            You don&apos;t own any other organizations to move this vault to. Create a new organization first.
          </div>
        ) : (
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='target-org'>Target Organization</Label>
              <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                <SelectTrigger id='target-org'>
                  <SelectValue placeholder='Select an organization' />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name} ({org.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='new-slug'>
                New Slug <span className='text-muted-foreground'>(optional)</span>
              </Label>
              <Input
                id='new-slug'
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder={vaultSlug}
              />
              {slugConflict && (
                <p className='text-sm text-destructive'>
                  A vault with this slug already exists in the target organization.
                </p>
              )}
              <p className='text-xs text-muted-foreground'>
                Leave empty to keep the current slug: <code>{vaultSlug}</code>
              </p>
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isMoving}>Cancel</AlertDialogCancel>
          <Button onClick={handleMove} disabled={!canMove || organizations.length === 0}>
            {isMoving ? <Spinner className='mr-2 h-4 w-4' /> : null}
            Move Vault
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
